import { useEffect, useState } from 'react';
import LoadMoreButton from './components/LoadMoreButton';
import useReviewQueries from './hooks/useReviewQueries';
import { fmtTime, fmtBytes, elapsed } from './utils/reviewFormat';

const STATUS = {
  0: { text: '진행 중', color: '#f59e0b' },
  1: { text: '완료', color: '#16a34a' },
  2: { text: '실패', color: '#ef4444' },
};

// 종류별 UI: 규칙·화면 구조는 같고 API 경로·입력 폼·표시 라벨만 다르다
const KIND_UI = {
  search: {
    api: '/api/admin/reviews',
    title: '리뷰 수집 요청 (사이트 1개 + 검색어 1개)',
    column: '검색어',
    label: (q) => q.keyword,
  },
  detail: {
    api: '/api/admin/details',
    title: '상세 수집 요청 (상품 페이지 URL 1개, 사이트는 URL로 자동 판별)',
    column: '상품 ID',
    label: (q) => q.product_id,
  },
};

const ERROR_TEXT = {
  unsupported_url: '지원하지 않는 URL입니다. 올리브영·세포라·울타·아마존(.com) 상품 페이지 URL을 넣어주세요.',
  url_required: '상품 페이지 URL을 입력하세요.',
  url_too_long: 'URL이 너무 깁니다.',
  keyword_required: '검색어를 입력하세요.',
  keyword_too_long: '검색어가 너무 깁니다(255자 이하).',
  invalid_site: '사이트를 선택하세요.',
  site_disabled: '이 사이트는 아직 수집이 설정되지 않았습니다(collector 미등록).',
  active_query_exists: '이미 진행 중인 질의가 있습니다. 끝난 뒤 다시 시도하세요.',
  s3_not_configured: '서버에 S3 설정이 없습니다.',
};
function errorText(data) {
  if (ERROR_TEXT[data.error]) return ERROR_TEXT[data.error];
  if (data.error === 'trigger_failed') return `BrightData 트리거 실패: ${data.message || ''}`;
  return data.message || data.error || '요청 실패';
}

const KO_MESSAGES = {
  errorText,
  reused: '같은 질의의 완료된 결과가 있어 그 결과를 표시합니다.',
  joined: '다른 사용자가 시작한 같은 질의에 합류했습니다.',
  started: '수집을 시작했습니다.',
  downloadFailed: '다운로드 실패',
};

const card = { background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: 16 };
const input = { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 };
const btn = (bg, disabled) => ({
  padding: '6px 16px', borderRadius: 6, border: 'none', fontSize: 14, fontWeight: 'bold', color: '#fff',
  background: disabled ? '#ccc' : bg, cursor: disabled ? 'not-allowed' : 'pointer',
});
const th = { padding: 6, textAlign: 'left', color: '#666', borderBottom: '1px solid #eee' };
const td = { padding: 6, borderBottom: '1px solid #f3f3f3' };

function StatusBadge({ status }) {
  const s = STATUS[status] || { text: String(status), color: '#aaa' };
  return <span style={{ color: s.color, fontWeight: 600 }}>{s.text}</span>;
}

export default function AdminReviews({ kind = 'search' }) {
  const ui = KIND_UI[kind] || KIND_UI.search;
  const api = ui.api;
  const isDetail = kind === 'detail';
  const [site, setSite] = useState('');
  const [keyword, setKeyword] = useState(''); // search: 검색어, detail: 상품 URL
  const {
    config, active, selected, history, cursor, submitting, downloading, notice, setNotice,
    busy, submit: send, selectQuery, loadMore, download: downloadAs,
  } = useReviewQueries(api, KO_MESSAGES);

  // config 로드 후 첫 활성 사이트 선택
  useEffect(() => {
    const first = config?.sites.find(s => s.enabled);
    if (first) setSite(first.key);
  }, [config]);

  const submit = (force = false) => {
    const kw = keyword.trim();
    let payload;
    if (isDetail) {
      const url = force && selected ? selected.url : kw;
      if (!url) { setNotice({ type: 'error', text: ERROR_TEXT.url_required }); return; }
      payload = { url, force };
    } else {
      const s = force && selected ? selected.site : site;
      const k = force && selected ? selected.keyword : kw;
      if (!s || !k) { setNotice({ type: 'error', text: '사이트와 검색어를 입력하세요.' }); return; }
      payload = { site: s, keyword: k, force };
    }
    send(payload);
  };

  const download = (fmt) => downloadAs(fmt, ui.label(selected));

  const siteLabel = (key) => config?.sites.find(s => s.key === key)?.label || key;

  return (
    <div>
      {/* 제출 폼 */}
      <div style={card}>
        <div style={{ fontSize: 14, color: '#555', marginBottom: 12 }}>{ui.title}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isDetail && (
            <select value={site} onChange={e => setSite(e.target.value)} style={input} disabled={busy}>
              {(config?.sites || []).map(s => (
                <option key={s.key} value={s.key} disabled={!s.enabled}>{s.label}{s.enabled ? '' : ' (비활성)'}</option>
              ))}
            </select>
          )}
          <input value={keyword} onChange={e => setKeyword(e.target.value)} placeholder={isDetail ? 'https://www.sephora.com/product/... 상품 페이지 URL' : '검색어'}
            style={{ ...input, flex: 1, minWidth: 200 }} disabled={busy}
            onKeyDown={e => { if (e.key === 'Enter' && !busy) submit(false); }} />
          <button onClick={() => submit(false)} disabled={busy} style={btn('#5a5af0', busy)}>
            {submitting ? '요청 중...' : active ? '진행 중...' : '수집'}
          </button>
        </div>
        {config && !isDetail && <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>max_products: {config.max_products} · 같은 질의는 기존 결과를 재사용합니다.</div>}
        {config && isDetail && (
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            지원 사이트: {config.sites.map(s => s.label + (s.enabled ? '' : '(비활성)')).join(' · ')} · 같은 상품(추적 파라미터·옵션만 다른 URL 포함)은 기존 결과를 재사용합니다. search 진행 중에도 제출할 수 있습니다.
          </div>
        )}
        {notice && <div style={{ fontSize: 13, color: notice.type === 'error' ? '#ef4444' : '#2563eb', marginTop: 8 }}>{notice.text}</div>}
      </div>

      {/* 진행 중 카드 */}
      {active && (
        <div style={card}>
          <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>내 진행 중 질의</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{siteLabel(active.site)} · {ui.label(active)}</div>
          <div style={{ fontSize: 13, color: '#888', marginTop: 6 }}>
            <StatusBadge status={active.status} /> · 경과 {elapsed(active.created)} · 요청자 {active.requester_email || '-'} · collection {active.collection_id || '(트리거 중)'}
          </div>
          <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>페이지를 떠나도 서버에서 계속 수집됩니다. 완료되면 자동으로 결과를 표시합니다.</div>
        </div>
      )}

      {/* 결과 패널 */}
      {selected && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontSize: 14, color: '#555' }}>결과</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{siteLabel(selected.site)} · {ui.label(selected)} <span style={{ fontSize: 13, color: '#888' }}>#{selected.id}</span></div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => download('json')} disabled={selected.status !== 1 || downloading} style={btn('#16a34a', selected.status !== 1 || downloading)}>JSON</button>
              <button onClick={() => download('csv')} disabled={selected.status !== 1 || downloading} style={btn('#16a34a', selected.status !== 1 || downloading)}>CSV</button>
              <button onClick={() => submit(true)} disabled={busy} style={btn('#ef4444', busy)}>다시 수집</button>
            </div>
          </div>
          <div style={{ fontSize: 13, color: '#555', marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span><StatusBadge status={selected.status} /></span>
            <span>상품 {selected.product_count ?? '-'}</span>
            <span>리뷰 {selected.review_count ?? '-'}</span>
            <span>실패 레코드 {selected.error_count ?? '-'}</span>
            <span>크기 {fmtBytes(selected.size_bytes)}</span>
            <span>소요 {elapsed(selected.created, selected.finished)}</span>
            <span>요청자 {selected.requester_email || '-'}</span>
            <span>완료 {fmtTime(selected.finished)}</span>
          </div>
          {isDetail && selected.url && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 6, wordBreak: 'break-all' }}>
              URL: <a href={selected.url_norm || selected.url} target="_blank" rel="noreferrer">{selected.url_norm || selected.url}</a>
            </div>
          )}
          {selected.error && <div style={{ fontSize: 13, color: '#ef4444', marginTop: 8 }}>오류: {selected.error}</div>}
          {Array.isArray(selected.summary) && selected.summary.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 12 }}>
              <thead><tr>
                <th style={th}>순위</th><th style={th}>상품</th><th style={th}>브랜드</th><th style={th}>가격</th>
                <th style={th}>평점</th><th style={th}>리뷰 수 / 수집</th><th style={th}>상태</th>
              </tr></thead>
              <tbody>
                {selected.summary.map((p, i) => (
                  <tr key={`${p.id || 'x'}-${i}`}>
                    <td style={td}>{p.search_rank ?? '-'}</td>
                    <td style={td}>{p.url ? <a href={p.url} target="_blank" rel="noreferrer">{p.name || p.id || '-'}</a> : (p.name || p.id || '-')}</td>
                    <td style={td}>{p.brand || '-'}</td>
                    <td style={td}>{p.price_text || '-'}</td>
                    <td style={td}>{p.rating ?? '-'}</td>
                    <td style={td}>{p.review_count ?? '-'} / {p.reviews_collected}</td>
                    <td style={{ ...td, color: p.error_code ? '#ef4444' : '#16a34a' }}>{p.error_code || 'ok'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {selected.status === 1 && (!selected.summary || selected.summary.length === 0) && (
            <div style={{ fontSize: 13, color: '#888', marginTop: 12 }}>수집된 상품이 없습니다.</div>
          )}
        </div>
      )}

      {/* 이력 */}
      <div style={card}>
        <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>질의 이력 (전체 사용자)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>
            <th style={th}>#</th><th style={th}>사이트</th><th style={th}>{ui.column}</th><th style={th}>상태</th>
            <th style={th}>상품/리뷰</th><th style={th}>요청자</th><th style={th}>요청 시각</th>
          </tr></thead>
          <tbody>
            {history.map(q => (
              <tr key={q.id} onClick={() => selectQuery(q.id)}
                style={{ cursor: 'pointer', background: selected?.id === q.id ? '#f5f5ff' : 'transparent' }}>
                <td style={td}>{q.id}</td>
                <td style={td}>{siteLabel(q.site)}</td>
                <td style={td}>{ui.label(q)}</td>
                <td style={td}><StatusBadge status={q.status} /></td>
                <td style={td}>{q.product_count ?? '-'} / {q.review_count ?? '-'}</td>
                <td style={td}>{q.requester_email || '-'}</td>
                <td style={td}>{fmtTime(q.created)}</td>
              </tr>
            ))}
            {history.length === 0 && <tr><td style={td} colSpan={7}><span style={{ color: '#aaa' }}>아직 질의가 없습니다.</span></td></tr>}
          </tbody>
        </table>
        <LoadMoreButton hasMore={!!cursor} onClick={loadMore} />
      </div>
    </div>
  );
}
